import type { Interpolation } from '../../types';
import { Config } from '../../video-data/config';
import type { Asset } from '../asset/Container';
import type { IconAsset } from '../asset/IconAsset';
import type { TimeAwareMediaAsset } from '../asset/TimeAwareMediaAsset';
import type { Slide } from '../slide/Slide';
import type { CircleMask } from '../mask/CircleMask';
import type { PolygonMask } from '../mask/PolygonMask';
import type { StencilMask } from '../mask/StencilMask';

export type MorphableProperty = 'position.x' | 'position.y' | 'scale.x' | 'scale.y' | 'alpha';

type MorphSourceState = {
    asset: Asset;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    alpha: number;
};

export class MorphManager {
    #slide: Slide;

    static #mediaTimeOffsets: Map<string, number> = new Map();
    static #allTargetAssets: Map<string, Asset> = new Map();

    static getMediaTimeOffset(assetId: string): number | null {
        return MorphManager.#mediaTimeOffsets.get(assetId) ?? null;
    }

    static isMorphTarget(assetId: string): boolean {
        return MorphManager.#allTargetAssets.has(assetId);
    }

    #sourceStates: Map<string, MorphSourceState> = new Map();
    #morphedMasks: Set<CircleMask | PolygonMask> = new Set();
    #targetMediaAssets: Set<TimeAwareMediaAsset> = new Set();
    #targetIconAssets: Set<IconAsset> = new Set();
    #targetAssets: Set<Asset> = new Set();

    constructor(slide: Slide) {
        this.#slide = slide;
    }

    setupMorphAnimations() {
        const nextScene = this.#slide.nextScene;
        if (!nextScene) {
            return;
        }

        const sourceAssets = this.#slide
            .getAssets({ deep: true })
            .sort((a, b) => a.name.localeCompare(b.name));
        const targetAssets = nextScene
            .getAssets({ deep: true })
            .sort((a, b) => a.name.localeCompare(b.name));

        const claimedTargetIds = new Set<string>();

        sourceAssets.forEach((sourceAsset) => {
            const matchingAsset = targetAssets.find(
                (targetAsset) =>
                    !claimedTargetIds.has(targetAsset.id) && sourceAsset.isMorphMatch(targetAsset),
            );
            if (!matchingAsset) {
                return;
            }

            claimedTargetIds.add(matchingAsset.id);
            this.#setupMorphForAssetPair(sourceAsset, matchingAsset);
        });

        for (const { asset } of this.#sourceStates.values()) {
            if (!asset.hasMorphAnimation) {
                continue;
            }
            MorphManager.#forceBuildAndDraw(asset);
        }
    }

    clearMorphAnimations() {
        for (const state of this.#sourceStates.values()) {
            const { asset, x, y, scaleX, scaleY, alpha } = state;

            if (!asset || asset.destroyed) {
                continue;
            }

            asset.clearMorphAnimations();

            asset.transformation.position.x = x;
            asset.transformation.position.y = y;
            asset.scale.x = scaleX;
            asset.scale.y = scaleY;
            asset.alpha = alpha;

            MorphManager.#forceBuildAndDraw(asset);
        }
        this.#sourceStates.clear();

        for (const mask of this.#morphedMasks) {
            mask._clearMorphState();
        }
        this.#morphedMasks.clear();

        for (const targetMedia of this.#targetMediaAssets) {
            MorphManager.#mediaTimeOffsets.delete(targetMedia.id);
        }
        this.#targetMediaAssets.clear();

        for (const targetIcon of this.#targetIconAssets) {
            MorphManager.#mediaTimeOffsets.delete(targetIcon.id);
        }
        this.#targetIconAssets.clear();

        for (const targetAsset of this.#targetAssets) {
            MorphManager.#allTargetAssets.delete(targetAsset.id);
            if (!targetAsset.destroyed) {
                MorphManager.#forceBuildAndDraw(targetAsset);
            }
        }
        this.#targetAssets.clear();
    }

    #setupMorphForAssetPair(sourceAsset: Asset, targetAsset: Asset) {
        const { duration: requestedDuration, interpolation } = sourceAsset.morphParameters;

        if (this.#slide.duration < requestedDuration) {
            return;
        }

        const morphDuration = requestedDuration;

        if (!this.#sourceStates.has(sourceAsset.id)) {
            this.#sourceStates.set(sourceAsset.id, {
                asset: sourceAsset,
                x: sourceAsset.transformation.position.x,
                y: sourceAsset.transformation.position.y,
                scaleX: sourceAsset.scale.x,
                scaleY: sourceAsset.scale.y,
                alpha: sourceAsset.alpha,
            });
        }

        this.#targetAssets.add(targetAsset);
        MorphManager.#allTargetAssets.set(targetAsset.id, targetAsset);

        if (Config.isMediaAsset(targetAsset)) {
            MorphManager.#mediaTimeOffsets.set(targetAsset.id, this.#slide.duration);
            this.#targetMediaAssets.add(targetAsset);
        }

        if (Config.isIconAsset(targetAsset)) {
            MorphManager.#mediaTimeOffsets.set(targetAsset.id, this.#slide.duration);
            this.#targetIconAssets.add(targetAsset);
        }

        this.#setupMaskMorph(sourceAsset, targetAsset, morphDuration, interpolation);

        MorphManager.#setupAnimations(sourceAsset, targetAsset, morphDuration, interpolation);
    }

    #setupMaskMorph(
        sourceAsset: Asset,
        targetAsset: Asset,
        morphDuration: number,
        interpolation: Interpolation,
    ) {
        const sourceMask = sourceAsset.mask as StencilMask;
        const targetMask = targetAsset.mask as StencilMask;

        if (!sourceMask || !targetMask) {
            return;
        }

        if (Config.isCircleMask(sourceMask) && Config.isCircleMask(targetMask)) {
            const sourceCircle = sourceMask as CircleMask;
            const targetCircle = targetMask as CircleMask;

            sourceCircle._setMorphState({
                startRadius: sourceCircle.endRadius,
                endRadius: targetCircle.endRadius,
                startCenter: { x: sourceCircle.centerX, y: sourceCircle.centerY },
                endCenter: { x: targetCircle.centerX, y: targetCircle.centerY },
            });
            this.#morphedMasks.add(sourceCircle);

            sourceCircle.transition = 0;

            const transitionAnim = sourceAsset.addMorphAnimation();
            transitionAnim.property = 'mask.transition';
            transitionAnim.startValue = 0;
            transitionAnim.endValue = 1;
            transitionAnim.duration = morphDuration;
            transitionAnim.interpolation = interpolation;
            transitionAnim.shouldOffsetPropertyValue = false;
        }

        if (Config.isPolygonMask(sourceMask) && Config.isPolygonMask(targetMask)) {
            const sourcePolygon = sourceMask as PolygonMask;
            const targetPolygon = targetMask as PolygonMask;

            if (sourcePolygon.endPoints.length !== targetPolygon.endPoints.length) {
                return;
            }

            const orderedSourceEndPoints = MorphManager.#orderPolygonPointsByCentroid(
                sourcePolygon.endPoints,
            );
            const orderedTargetEndPoints = MorphManager.#orderPolygonPointsByCentroid(
                targetPolygon.endPoints,
            );

            if (orderedSourceEndPoints.some(isNaN) || orderedTargetEndPoints.some(isNaN)) {
                return;
            }

            sourcePolygon._setMorphState({
                startPoints: orderedSourceEndPoints,
                endPoints: orderedTargetEndPoints,
            });
            this.#morphedMasks.add(sourcePolygon);

            sourcePolygon.transition = 0;

            const transitionAnim = sourceAsset.addMorphAnimation();
            transitionAnim.property = 'mask.transition';
            transitionAnim.startValue = 0;
            transitionAnim.endValue = 1;
            transitionAnim.duration = morphDuration;
            transitionAnim.interpolation = interpolation;
            transitionAnim.shouldOffsetPropertyValue = false;
        }

        if (Config.isMediaAsset(sourceAsset) && Config.isMediaAsset(targetAsset)) {
            MorphManager.#setupMediaTransformAnimations(
                sourceAsset,
                targetAsset,
                morphDuration,
                interpolation,
            );
        }
    }

    static #setupMediaTransformAnimations(
        sourceAsset: TimeAwareMediaAsset,
        targetAsset: TimeAwareMediaAsset,
        morphDuration: number,
        interpolation: Interpolation,
    ) {
        const properties = [
            { property: 'scaleX', source: sourceAsset.scaleX, target: targetAsset.scaleX },
            { property: 'scaleY', source: sourceAsset.scaleY, target: targetAsset.scaleY },
            {
                property: 'xTranslation',
                source: sourceAsset.xTranslation,
                target: targetAsset.xTranslation,
            },
            {
                property: 'yTranslation',
                source: sourceAsset.yTranslation,
                target: targetAsset.yTranslation,
            },
        ];

        for (const { property, source, target } of properties) {
            if (source === target) {
                continue;
            }

            const anim = sourceAsset.addMorphAnimation();
            anim.property = property;
            anim.startValue = source;
            anim.endValue = target;
            anim.duration = morphDuration;
            anim.interpolation = interpolation;
            anim.shouldOffsetPropertyValue = false;
        }
    }

    static #setupAnimations(
        sourceAsset: Asset,
        targetAsset: Asset,
        morphDuration: number,
        interpolation: Interpolation,
    ) {
        MorphManager.#forceBuildAndDraw(sourceAsset);
        MorphManager.#forceBuildAndDraw(targetAsset);

        const excludedProperties = sourceAsset.morphExcludedProperties;
        const animationConfigs = MorphManager.#buildAnimationConfigs(sourceAsset, targetAsset);

        Object.entries(animationConfigs).forEach(([morphableProperty, config]) => {
            if (excludedProperties.includes(morphableProperty as MorphableProperty)) {
                return;
            }

            const sourceAnim = sourceAsset.addMorphAnimation();
            sourceAnim.property = config.property;
            sourceAnim.startValue = config.startValue;
            sourceAnim.endValue = config.endValue;
            sourceAnim.duration = morphDuration;
            sourceAnim.interpolation = interpolation;
            sourceAnim.shouldOffsetPropertyValue = config.shouldOffsetPropertyValue;
        });
    }

    static #buildAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const sourcePos = sourceAsset.transformation.position;
        const targetPos = targetAsset.transformation.position;
        const deltaX = targetPos.x - sourcePos.x;
        const deltaY = targetPos.y - sourcePos.y;

        const scaleRatioX = sourceAsset.width > 0 ? targetAsset.width / sourceAsset.width : 1;
        const scaleRatioY = sourceAsset.height > 0 ? targetAsset.height / sourceAsset.height : 1;

        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        const hasMask = Boolean(sourceAsset.mask);

        if (deltaX !== 0 && !hasMask) {
            configs['position.x'] = {
                property: 'transformation.position.x',
                shouldOffsetPropertyValue: true,
                startValue: 0,
                endValue: deltaX,
            };
        }

        if (deltaY !== 0 && !hasMask) {
            configs['position.y'] = {
                property: 'transformation.position.y',
                shouldOffsetPropertyValue: true,
                startValue: 0,
                endValue: deltaY,
            };
        }

        if (scaleRatioX !== 1 && !hasMask) {
            configs['scale.x'] = {
                property: 'scale.x',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioX,
            };
        }

        if (scaleRatioY !== 1 && !hasMask) {
            configs['scale.y'] = {
                property: 'scale.y',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioY,
            };
        }

        if (sourceAsset.alpha !== targetAsset.alpha) {
            configs['alpha'] = {
                property: 'alpha',
                shouldOffsetPropertyValue: false,
                startValue: sourceAsset.alpha,
                endValue: targetAsset.alpha,
            };
        }

        return configs;
    }

    static #orderPolygonPointsByCentroid(points: number[]): number[] {
        const numPoints = points.length / 2;

        let centroidX = 0;
        let centroidY = 0;
        for (let i = 0; i < numPoints; i++) {
            centroidX += points[i * 2];
            centroidY += points[i * 2 + 1];
        }
        centroidX /= numPoints;
        centroidY /= numPoints;

        const pointsArray = Array.from({ length: numPoints }, (_, i) => {
            const x = points[i * 2];
            const y = points[i * 2 + 1];
            const angle = Math.atan2(y - centroidY, x - centroidX);
            return { x, y, angle };
        });

        pointsArray.sort((a, b) => a.angle - b.angle);

        return pointsArray.flatMap((point) => [point.x, point.y]);
    }

    static #forceBuildAndDraw(asset: Asset) {
        const prevAutoDraw = asset.autoDraw;
        asset.autoDraw = true;
        asset.requiresRedraw = true;
        asset._buildAndDrawRecur();
        asset.autoDraw = prevAutoDraw;
    }
}
